<template>
  <div class="relative w-full h-full rounded-md overflow-hidden bg-black/60 flex items-center justify-center">
    <!-- Video renders when a video track is live and camera is on. Audio-
         only / muted-video tiles show the label placeholder instead. -->
    <video
      v-if="stream && !videoOff"
      ref="videoEl"
      :muted="muted"
      autoplay
      playsinline
      class="w-full h-full object-cover"
      :class="isSelf ? 'scale-x-[-1]' : ''"
    />
    <div v-else class="text-xs text-text-muted font-semibold text-center px-2">
      {{ label }}
    </div>

    <!-- Corner badges: mic off + speaker identity -->
    <div class="absolute bottom-1 left-1 right-1 flex items-end justify-between gap-2 pointer-events-none">
      <span class="text-[10px] text-white bg-black/60 px-1.5 py-0.5 rounded truncate max-w-full">
        {{ label }}
      </span>
      <span
        v-if="audioOff"
        class="w-5 h-5 rounded-full bg-red-500/80 text-white flex items-center justify-center shrink-0"
      >
        <el-icon size="10"><Mute /></el-icon>
      </span>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onBeforeUnmount } from 'vue'
import { Mute } from '@element-plus/icons-vue'

/**
 * One tile in the call window's grid. Binds a `MediaStream` to a
 * <video> element imperatively — setting `.srcObject` via :prop doesn't
 * work because Vue serialises props as strings.
 *
 * `isSelf` mirrors the local tile left-to-right so the user sees their
 * face the normal way.
 */
const props = defineProps({
  stream: { type: MediaStream, default: null },
  muted: { type: Boolean, default: false },   // audio muted on this <video> element
  isSelf: { type: Boolean, default: false },
  label: { type: String, default: '' },
  audioOff: { type: Boolean, default: false }, // track-level mute indicator
  videoOff: { type: Boolean, default: false }
})

const videoEl = ref(null)

// Imperatively hook the MediaStream up — .srcObject is a runtime-only API
// and can't be set via a :prop binding.
watch([videoEl, () => props.stream], ([el, stream]) => {
  if (!el) return
  if (stream && el.srcObject !== stream) {
    el.srcObject = stream
    applyOutputDevice(el)
  }
  if (!stream && el.srcObject) {
    el.srcObject = null
  }
}, { flush: 'post' })

// Route the remote tile's audio to the user's chosen output device. Only
// runs on remote tiles (own tile is muted via :muted="muted") so the local
// preview never bleeds back to the user's own speakers.
//
// On failure (stale sinkId after a USB headset is unplugged, browser
// refusing the id, …) explicitly revert to the system default with
// `setSinkId('')` — otherwise the element keeps routing to whatever sink
// was last set, which can be silent. Errors stay non-fatal so a single
// bad tile doesn't break the rest of the grid.
const { user } = useUserSession()
async function applyOutputDevice(el) {
  if (!el || props.isSelf) return
  if (typeof el.setSinkId !== 'function') return
  const sinkId = user.value?.chatProfile?.audioOutputDeviceId
  if (!sinkId) {
    // User cleared their picked output — restore default explicitly so a
    // previously-set custom sink doesn't linger on this element.
    try { await el.setSinkId('') } catch { /* noop */ }
    return
  }
  try {
    await el.setSinkId(sinkId)
  } catch (err) {
    console.warn('[chat:call:tile] setSinkId failed, reverting to default', err)
    try { await el.setSinkId('') } catch { /* noop */ }
  }
}

// React to live setting changes — when the user picks a new speaker in
// the settings dialog while on a call, re-route without a reconnect.
// Also re-fires when the remote toggles their camera back on (re-mount of
// the <video> element triggers the videoEl ref change in the other watcher).
watch(() => user.value?.chatProfile?.audioOutputDeviceId, () => {
  if (videoEl.value) applyOutputDevice(videoEl.value)
})

onBeforeUnmount(() => {
  if (videoEl.value) videoEl.value.srcObject = null
})
</script>
